import {
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  Paper,
  Spoiler,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import {
  PiArrowCounterClockwise,
  PiArrowUUpLeft,
  PiCheck,
  PiCheckCircle,
  PiCircleDashed,
  PiInfo,
  PiPencilSimple,
  PiWarningCircle,
} from 'react-icons/pi';
import { LineRef, lineKey } from '@/models/lineKey';
import {
  TSplitPlanItem,
  useAssignLines,
  useReviewSplit,
} from '@/models/split-plan';
import SplitScheduleBuilder from './SplitScheduleBuilder';
import {
  DraftInstallment,
  deriveSplit,
  money,
  OrderedService,
  SplitDerived,
} from './splitDerive';

/**
 * `DraftInstallment.key` is `item-<split_plan_item.id>` for a persisted row — see
 * `itemsToDraft`. The builder also mints `new-<timestamp>-<seq>` keys for rows added in
 * the editor; those have no plan item and must never reach the save payload as `NaN`, so
 * this matches strictly and returns null for anything else.
 */
const planItemIdOf = (key: string): number | null => {
  const m = /^item-(\d+)$/.exec(key);
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isSafeInteger(id) ? id : null;
};

/** Debounce before an assignment change is persisted. Long enough that a burst of
 *  drags is one request, short enough that the reviewer never waits on it. */
const AUTOSAVE_MS = 600;

export default function SplitApprovalPanel({
  offerId,
  currency,
  installments,
  services,
  accountId,
  items,
  srNote,
  orderNetMinor,
}: {
  offerId: number;
  currency: string;
  installments: DraftInstallment[];
  services: OrderedService[];
  accountId: number;
  items: TSplitPlanItem[];
  srNote: string | null;
  /** The order's value in minor units, as the server measures it. */
  orderNetMinor?: number | null;
}) {
  const review = useReviewSplit(offerId);
  const assign = useAssignLines(offerId);
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState('');
  const [withAlt, setWithAlt] = useState(false);
  // BizDev's editable counter-schedule, seeded from the SR's proposal.
  const [alt, setAlt] = useState<DraftInstallment[]>([]);
  const altDerived = deriveSplit(alt, services, orderNetMinor);

  // Live assignment, seeded from the persisted covered lines. The SR's amounts and
  // installment count ride along untouched — BizDev distributes services, nothing else.
  const [draft, setDraft] = useState<DraftInstallment[]>(() =>
    installments.map((i) => ({ ...i })),
  );
  const derived = useMemo(
    () => deriveSplit(draft, services, orderNetMinor),
    [draft, services, orderNetMinor],
  );

  // The canvas shows one schedule at a time: the SR's proposal while reviewing it, or
  // the counter-proposal while composing one. Two builders on one page was the old
  // shape, and it left the reviewer comparing schedules by scrolling.
  const showingAlt = returning && withAlt;
  const active = showingAlt ? altDerived : derived;

  const refByKey = useMemo(() => {
    const m = new Map<string, LineRef>();
    for (const s of services) m.set(lineKey(s.lineRef), s.lineRef);
    return m;
  }, [services]);

  // Order-level money, which the per-installment figures cannot show: totals per
  // currency, the EUR reconciliation when the order is mixed, and the margin.
  const totals = useMemo(() => {
    const saleByCcy: Record<string, number> = {};
    const cogsByCcy: Record<string, number> = {};
    let cogsEur = 0;
    for (const s of services) {
      saleByCcy[s.currency] = (saleByCcy[s.currency] ?? 0) + s.saleValue;
      cogsByCcy[s.currency] = (cogsByCcy[s.currency] ?? 0) + s.cogs;
      cogsEur += s.cogsEur ?? 0;
    }
    const currencies = Object.keys(saleByCcy);
    const mixed =
      currencies.length > 1 ||
      (currencies.length === 1 && currencies[0] !== 'EUR');
    return { saleByCcy, cogsByCcy, cogsEur, mixed };
  }, [services]);

  const assignmentPayload = useMemo(
    () =>
      draft.flatMap((inst) => {
        const planItemId = planItemIdOf(inst.key);
        if (planItemId === null) return [];
        return [
          {
            planItemId,
            lines: inst.lines
              .map((r) => refByKey.get(lineKey(r)))
              .filter((r): r is LineRef => !!r),
            note: inst.note ?? '',
          },
        ];
      }),
    [draft, refByKey],
  );

  const assignmentDirty = useMemo(
    () =>
      items.some((it) => {
        const saved = it.coveredLines.map((l) => lineKey(l)).sort();
        const inst = draft.find((i) => planItemIdOf(i.key) === it.id);
        const local = (inst?.lines ?? []).map((l) => lineKey(l)).sort();
        if ((inst?.note ?? '').trim() !== (it.note ?? '').trim()) return true;
        return (
          saved.length !== local.length || saved.some((k, i) => k !== local[i])
        );
      }),
    [items, draft],
  );

  // Persisting is not the reviewer's job. A separate Save button meant an approval
  // could be blocked by a step nobody remembered to take, so every drop saves itself.
  // `mutate` is referentially stable in TanStack Query v5, so it can sit in the deps
  // without re-arming the timer on every render.
  const saveAssignment = assign.mutate;
  useEffect(() => {
    if (!assignmentDirty || showingAlt) return;
    const t = setTimeout(() => saveAssignment(assignmentPayload), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [assignmentDirty, assignmentPayload, showingAlt, saveAssignment]);

  const saveState: 'saving' | 'failed' | 'saved' = assign.isPending
    ? 'saving'
    : assign.isError
      ? 'failed'
      : assignmentDirty
        ? 'saving'
        : 'saved';

  const approveBlockedReason = !derived.allAssigned
    ? `${services.length - derived.assignedCount} service${
        services.length - derived.assignedCount === 1 ? '' : 's'
      } still unassigned`
    : saveState === 'failed'
      ? 'The assignment could not be saved'
      : saveState === 'saving'
        ? 'Saving the assignment…'
        : '';

  // An alternative the SR adopts unchanged goes straight to the customer, so it has to
  // clear the same bar as an approval: amounts that reconcile AND every selected
  // service distributed. Catching it here beats the SR discovering it on adopt.
  const altUnassigned = services.length - altDerived.assignedCount;
  const altBlockedReason = !withAlt
    ? ''
    : !altDerived.canSubmit
      ? `The alternative's installment amounts must add up to ${money(
          derived.orderedTotal,
          currency,
        )}.`
      : altUnassigned > 0
        ? `Distribute the remaining ${altUnassigned} service${
            altUnassigned === 1 ? '' : 's'
          } across the alternative's installments — the SR can adopt it as it stands, so it must be complete.`
        : '';

  const seedAlt = () =>
    setAlt(installments.map((i) => ({ ...i, key: `alt-${i.key}` })));

  const startReturn = () => {
    seedAlt();
    setReturning(true);
  };

  const approve = () => review.mutate({ decision: 'approve' });

  const submitReturn = () => {
    const altItems =
      withAlt && alt.length > 0
        ? [...alt]
            .sort((a, b) => a.seq - b.seq)
            .map((i) => ({
              seq: i.seq,
              currency: i.currency,
              paymentMethod: i.paymentMethod,
              payerId: i.payerId ?? null,
              amount: i.amount ?? null,
              // Draft lines may carry coverage metadata (coverageId, deliveredAt);
              // the API takes bare line refs and rejects anything else.
              lines: i.lines
                .map((r) => refByKey.get(lineKey(r)))
                .filter((r): r is LineRef => !!r),
              note: i.note ?? null,
            }))
        : undefined;
    review.mutate(
      { decision: 'return', note: note.trim() || undefined, altItems },
      { onSuccess: () => setReturning(false) },
    );
  };

  return (
    <Stack gap="md">
      <Scoreboard
        derived={active}
        totals={totals}
        currency={currency}
        serviceCount={services.length}
        alt={showingAlt}
      />

      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper withBorder p="md" data-testid="ux-review-canvas">
            <Stack gap="sm">
              {showingAlt ? (
                <Alert
                  color="indigo"
                  variant="light"
                  icon={<PiPencilSimple size={16} />}
                  title="Alternative schedule"
                >
                  <Group justify="space-between" align="center" wrap="wrap">
                    <Text fz="xs">
                      Baselined on the SR's proposal — {installments.length}{' '}
                      installment
                      {installments.length === 1 ? '' : 's'},{' '}
                      {money(derived.orderedTotal, currency)}. Change the
                      amounts, the installment count and the distribution; the
                      SR sees a before/after diff.
                    </Text>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      leftSection={<PiArrowCounterClockwise size={12} />}
                      onClick={seedAlt}
                    >
                      Reset to SR proposal
                    </Button>
                  </Group>
                </Alert>
              ) : (
                <Text fz="xs" c="dimmed">
                  Drag each service into the installment that covers it. The
                  amounts and the number of installments are the SR's proposal
                  and are read-only — return the plan to change them.
                </Text>
              )}

              {/* One builder, two modes. Reviewing the SR's proposal keeps the
                  amounts read-only while the drag targets stay live; composing an
                  alternative unlocks the whole schedule. */}
              <SplitScheduleBuilder
                key={showingAlt ? 'alt' : 'review'}
                installments={showingAlt ? alt : draft}
                setInstallments={showingAlt ? setAlt : setDraft}
                editable={showingAlt}
                assignEditable
                showNote
                showPricingHeader={showingAlt}
                services={services}
                currency={currency}
                accountId={accountId}
                derived={active}
              />
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box
            style={{ position: 'sticky', top: 8 }}
            data-testid="ux-decision-rail"
          >
            <Stack gap="sm">
              {srNote && srNote.trim() && (
                <Alert
                  color="blue"
                  variant="light"
                  icon={<PiInfo size={16} />}
                  title="Note from SR"
                  p="xs"
                >
                  <Spoiler maxHeight={72} showLabel="More" hideLabel="Less">
                    <Text fz="xs" style={{ whiteSpace: 'pre-wrap' }}>
                      {srNote}
                    </Text>
                  </Spoiler>
                </Alert>
              )}

              <Paper withBorder p="md">
                {!returning ? (
                  <Stack gap="sm">
                    <Text fw={600} fz="sm">
                      Review decision
                    </Text>

                    <Stack gap={6}>
                      <ChecklistRow
                        done={derived.allAssigned}
                        label={
                          derived.allAssigned
                            ? 'Every service is assigned'
                            : approveBlockedReason
                        }
                      />
                      <ChecklistRow
                        done={saveState === 'saved'}
                        failed={saveState === 'failed'}
                        label={
                          saveState === 'saved'
                            ? 'Assignment saved'
                            : saveState === 'saving'
                              ? 'Saving assignment…'
                              : 'Assignment not saved'
                        }
                        action={
                          saveState === 'failed' ? (
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              color="red"
                              onClick={() => assign.mutate(assignmentPayload)}
                            >
                              Retry
                            </Button>
                          ) : undefined
                        }
                      />
                      {!derived.cogsCovered && (
                        <ChecklistRow
                          warning
                          label={`Installment 1 is short of the order COGS by ${money(
                            Math.max(
                              derived.cogsTotal - derived.firstInstallmentCogs,
                              0,
                            ),
                            currency,
                          )}`}
                        />
                      )}
                    </Stack>

                    <Divider />

                    <Stack gap="xs">
                      <Tooltip
                        label={approveBlockedReason}
                        disabled={!approveBlockedReason}
                      >
                        <span>
                          <Button
                            fullWidth
                            size="sm"
                            color="teal"
                            leftSection={<PiCheck size={14} />}
                            loading={review.isPending}
                            disabled={!!approveBlockedReason}
                            onClick={approve}
                          >
                            Approve schedule
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        fullWidth
                        size="sm"
                        variant="light"
                        color="orange"
                        leftSection={<PiArrowUUpLeft size={14} />}
                        onClick={startReturn}
                      >
                        Return to SR
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack gap="sm">
                    <Text fw={600} fz="sm">
                      Return to SR
                    </Text>
                    <Textarea
                      size="xs"
                      label="Reason"
                      placeholder="Why the proposed schedule is not feasible, and any alternative the agreement permits"
                      autosize
                      minRows={3}
                      value={note}
                      onChange={(e) => setNote(e.currentTarget.value)}
                    />

                    <Button
                      size="compact-xs"
                      variant={withAlt ? 'filled' : 'light'}
                      color="indigo"
                      leftSection={<PiPencilSimple size={12} />}
                      onClick={() => setWithAlt((v) => !v)}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      {withAlt
                        ? 'Discard alternative schedule'
                        : 'Propose an alternative schedule'}
                    </Button>
                    {withAlt && (
                      <Text fz="xs" c="dimmed">
                        Editing the alternative on the left. The SR's proposal
                        is kept and shown to them as a diff.
                      </Text>
                    )}

                    <Divider />

                    <Group gap="xs" grow>
                      <Button
                        size="sm"
                        color="orange"
                        loading={review.isPending}
                        onClick={submitReturn}
                        disabled={!!altBlockedReason}
                      >
                        Return to SR
                      </Button>
                      <Button
                        size="sm"
                        variant="subtle"
                        onClick={() => {
                          setReturning(false);
                          setWithAlt(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Group>
                    {altBlockedReason && (
                      <Text fz="xs" c="red">
                        {altBlockedReason}
                      </Text>
                    )}
                  </Stack>
                )}
              </Paper>
            </Stack>
          </Box>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

/**
 * The acceptance criteria of the review, in one sticky strip: how much money is on the
 * table, whether the first installment covers what the order costs us, and how much
 * assignment work is left. It carries the order-level figures that the per-installment
 * rows cannot — currency totals, the EUR reconciliation of a mixed order, and margin.
 */
function Scoreboard({
  derived: d,
  totals,
  currency,
  serviceCount,
  alt,
}: {
  derived: SplitDerived;
  totals: {
    saleByCcy: Record<string, number>;
    cogsByCcy: Record<string, number>;
    cogsEur: number;
    mixed: boolean;
  };
  currency: string;
  serviceCount: number;
  alt: boolean;
}) {
  const fmtByCcy = (byCcy: Record<string, number>) =>
    Object.entries(byCcy)
      .map(([c, v]) => money(v, c))
      .join(' + ') || money(0, currency);

  const margin = d.orderedTotal - d.cogsTotal;
  const marginPct = d.orderedTotal > 0 ? (margin / d.orderedTotal) * 100 : 0;

  return (
    <Paper
      withBorder
      p="sm"
      bg="var(--mantine-color-body)"
      style={{ position: 'sticky', top: 0, zIndex: 3 }}
      data-testid="ux-review-scoreboard"
    >
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group gap="lg" wrap="wrap">
          <Figure label="Sale" value={fmtByCcy(totals.saleByCcy)} />
          <Figure
            label="COGS"
            value={fmtByCcy(totals.cogsByCcy)}
            hint={
              totals.mixed ? `≈ ${money(totals.cogsEur, 'EUR')}` : undefined
            }
            dimmed
          />
          {!totals.mixed && (
            <Figure
              label="Margin"
              value={money(margin, currency)}
              hint={`${Math.round(marginPct)}%`}
            />
          )}
          <Figure
            label={alt ? 'Alternative billed' : 'Billed'}
            value={money(d.amountsTotal, currency)}
            hint={
              d.amountsSumToTotal
                ? undefined
                : `of ${money(d.orderedTotal, currency)}`
            }
          />
        </Group>

        <Group gap="xs" wrap="wrap">
          <Badge
            size="sm"
            variant="light"
            color={d.allAssigned ? 'teal' : 'orange'}
            leftSection={
              d.allAssigned ? (
                <PiCheck size={12} />
              ) : (
                <PiCircleDashed size={12} />
              )
            }
          >
            {d.assignedCount} of {serviceCount} assigned
          </Badge>
          <Tooltip label="An installment 1 that covers the whole order COGS makes an unpaid later installment a minimal loss.">
            <Badge
              size="sm"
              variant="light"
              color={d.cogsCovered ? 'teal' : 'orange'}
              leftSection={
                d.cogsCovered ? (
                  <PiCheck size={12} />
                ) : (
                  <PiWarningCircle size={12} />
                )
              }
            >
              {d.cogsCovered ? 'Inst. 1 covers COGS' : 'Inst. 1 short of COGS'}
            </Badge>
          </Tooltip>
        </Group>
      </Group>
    </Paper>
  );
}

function Figure({
  label,
  value,
  hint,
  dimmed,
}: {
  label: string;
  value: string;
  hint?: string;
  dimmed?: boolean;
}) {
  return (
    <Stack gap={0}>
      <Text fz="xs" c="dimmed">
        {label}
      </Text>
      <Group gap={6} wrap="nowrap" align="baseline">
        <Text fz="sm" fw={600} c={dimmed ? 'dimmed' : undefined}>
          {value}
        </Text>
        {hint && (
          <Text fz="xs" c="dimmed">
            {hint}
          </Text>
        )}
      </Group>
    </Stack>
  );
}

function ChecklistRow({
  done,
  failed,
  warning,
  label,
  action,
}: {
  done?: boolean;
  failed?: boolean;
  warning?: boolean;
  label: string;
  action?: React.ReactNode;
}) {
  const color = failed ? 'red' : warning ? 'orange' : done ? 'teal' : 'orange';
  const Icon =
    failed || warning ? PiWarningCircle : done ? PiCheckCircle : PiCircleDashed;

  return (
    <Group gap={6} wrap="nowrap" align="center">
      <Text component="span" c={color} style={{ lineHeight: 0 }}>
        <Icon size={14} />
      </Text>
      <Text fz="xs" c={done && !warning ? 'dimmed' : undefined}>
        {label}
      </Text>
      {action}
    </Group>
  );
}
